/**
 * AmplifiersView — per-publication voice overrides and the amplification
 * rules that fan content out to connected social accounts.
 *
 * S-067 / FEAT-006 (Phase 6). Replaces the AdminStubs.AmplifiersView
 * placeholder. Admin-only screen reachable from Settings → Amplification.
 * The route path (/settings/amplifiers) is unchanged on purpose — the page
 * was re-labelled, not moved.
 *
 * FEAT-095 / S-401 (CCM-79), ADR-019 — the social-dispatch half of this
 * page is gone. `amplifiers` is retired as a social-dispatch concept and
 * survives only as a voice-profile-override carrier (the S-375 feature
 * VoiceProfilesIndexView.tsx reads to decide whether a profile is safe to
 * delete). Connecting a social account happens in exactly one place now:
 * Settings → Social Accounts. Removed here: ZernioConnectControl and its
 * call sites, the DISPATCH_PROVIDERS registry, and the
 * *DispatchAccountId-driven "configured platforms" derivation. The four
 * *_dispatch_account_id columns still exist on the row but nothing in the
 * app reads or writes them anymore (ADR-019's Consequences).
 *
 * Schema reality check (db/schema.ts lines 455-497, 506-537):
 *   - amplifiers are scoped per (tenant, agencyclient, publication).
 *   - LinkedIn carries additional config (preferred_time, time_zone,
 *     amp_offset_days) — per-amplifier timing tweaks, unrelated to the
 *     retired dispatch surface and still live.
 *   - amplification_rules.applies_to_publication_id binds a rule to a
 *     publication. Each rule has a `steps` jsonb array; each step
 *     references a social account by id (S-401 — account_id replaced
 *     amplifier_slug + platform).
 *
 * UX shape:
 *   - Workspace selector (top, useActiveWorkspace().activate on change).
 *   - Publication selector (second row, local state — voice overrides &
 *     rules are both publication-scoped).
 *   - Two stacked sections:
 *       Voice Overrides — card grid. Click a card → edit drawer.
 *         "+ New voice override" button on the section header.
 *       Amplification Rules — list of rules for the current publication
 *         plus per-rule expand of `steps`. Click a rule → edit drawer
 *         with a repeating step editor (account + offset + time-of-day).
 *   - Both sections share the same workspace/publication scope and a
 *     single page refresh.
 *
 * Form fields surfaced (subset of full schema; can extend later):
 *   Voice override (an `amplifiers` row):
 *     - display_name (text), slug (kebab-case)
 *     - voice_profile_override_id (dropdown of workspace voice profiles)
 *     - linkedin: preferred_time, time_zone, amp_offset_days
 *   Rule:
 *     - name, slug
 *     - steps array of { account_id, offset_days?, time_of_day? } —
 *       repeating sub-form
 *
 * Edge cases:
 *   - No workspaces → empty state pointing at /settings/workspaces.
 *   - No publications → empty state pointing at /settings/publications.
 *   - No voice profiles in the workspace → voice override dropdown
 *     shows only "(none)"; hint guides the user to create a voice
 *     profile under Publications.
 *   - Deletion cascade: deleting a voice override hard-deletes any
 *     pre-migration social_calendar rows that still carry its
 *     amplifier_id (ON DELETE CASCADE — the cascade behind BUG-088).
 *     The confirm copy warns about this. Account-based rows are safe:
 *     account_id is ON DELETE SET NULL.
 */

import { useEffect, useMemo, useState } from "react";
// Consolidation (option 3): shared admin modal/drawer shell. Replaces the
// local ModalShell/DrawerShell that used to live at the bottom of this file.
import { ModalShell, DrawerShell } from "@/components/SettingsModalShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { LINKEDIN_ARTICLE_PLATFORM, toEditableStep } from "@/lib/amplification-steps";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

// Platform enum used to label connected social accounts in the rule
// step picker. Matches social_accounts.platform.
const PLATFORMS = ["linkedin", "x", "instagram", "threads"] as const;
type Platform = (typeof PLATFORMS)[number];

const PLATFORM_LABEL: Record<Platform, string> = {
  linkedin: "LinkedIn",
  x: "X (Twitter)",
  instagram: "Instagram",
  threads: "Threads",
};

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface Publication {
  id: number;
  name: string;
  slug: string;
}

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

interface VoiceProfile {
  id: number;
  name: string;
}

interface VoiceProfilesResponse {
  data: VoiceProfile[];
  meta: { total: number; limit: number; offset: number };
}

interface Amplifier {
  id: number;
  tenantId?: number;
  agencyclientId?: number;
  agencyclient_id?: number;
  publicationId?: number;
  publication_id?: number;
  slug: string;
  displayName?: string;
  display_name?: string;
  voiceProfileOverrideId?: number | null;
  voice_profile_override_id?: number | null;
  linkedinPreferredTime?: string | null;
  linkedin_preferred_time?: string | null;
  linkedinTimeZone?: string | null;
  linkedin_time_zone?: string | null;
  linkedinAmpOffsetDays?: number;
  linkedin_amp_offset_days?: number;
  // FEAT-095 / S-401 (ADR-019) — the four *_dispatch_account_id columns and
  // social_dispatch_provider still exist on the row, but nothing in the app
  // reads them anymore, so they are deliberately absent from this type.
  createdAt?: string;
  created_at?: string;
}

interface AmplifiersResponse {
  data: Amplifier[];
  meta: { total: number; limit: number; offset: number };
}

// S-401 (CCM-79) — account_id replaces amplifier_slug + platform as of this
// story. `platform` stays as an optional explicit override (used by exactly
// one migrated rule, the LinkedIn newsletter's manual-only companion post —
// see bin/migrate-amplification-rule-to-accounts.ts); this picker never
// sets it for a step it creates.
interface AmplificationStep {
  account_id: number;
  platform?: string;
  offset_days?: number;
  time_of_day?: string;
}

interface RuleAccountOption {
  id: number;
  platform: string;
  display_name: string;
  is_shared_to_me: boolean;
}

interface AmplificationRule {
  id: number;
  tenantId?: number;
  agencyclientId?: number;
  agencyclient_id?: number;
  appliesToPublicationId?: number;
  applies_to_publication_id?: number;
  slug: string;
  name: string;
  steps?: AmplificationStep[];
  createdAt?: string;
  created_at?: string;
}

interface AmplificationRulesResponse {
  data: AmplificationRule[];
  meta: { total: number; limit: number; offset: number };
}

const AMP_WS_KEY = "ccm:amplifiers:active_ws";
const AMP_PUB_KEY = "ccm:amplifiers:active_pub";

// ---------- Helpers ----------

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function isValidSlug(s: string): boolean {
  if (s.length < 1 || s.length > 80) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(s);
}

function readAmplifierField<T>(
  amp: Amplifier,
  camel: keyof Amplifier,
  snake: keyof Amplifier,
  fallback: T
): T {
  const v = (amp[camel] ?? amp[snake]) as T | undefined;
  return (v ?? fallback) as T;
}

function ampDisplayName(amp: Amplifier): string {
  return amp.displayName ?? amp.display_name ?? amp.slug;
}

function ampVoiceOverrideId(amp: Amplifier): number | null {
  return amp.voiceProfileOverrideId ?? amp.voice_profile_override_id ?? null;
}

// ---------- Top-level view ----------

export function AmplifiersView() {
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(AMP_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedWsId != null) {
      try {
        window.sessionStorage.setItem(AMP_WS_KEY, String(selectedWsId));
      } catch {
        /* tolerate */
      }
    }
  }, [selectedWsId]);

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  if (wsLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading amplification settings…</div>
        </div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {wsError instanceof ApiError && wsError.status === 403
              ? "You don't have permission to view amplification settings."
              : (wsError.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <Header />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings → Workspaces, then add a
              publication, before configuring amplification.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <AmplifiersBody
      workspaces={workspaces}
      selectedWsId={selectedWsId}
      onSelectWs={setSelectedWsId}
    />
  );
}

function Header() {
  return (
    <>
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>Settings</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>Amplification</span>
      </nav>
      <SectionHeader
        eyebrow="Settings · Amplification"
        title="Amplification"
        description="Voice overrides that change how a publication sounds when amplified, and the rules that decide which connected accounts fire when a piece publishes. Connect accounts under Settings → Social Accounts."
      />
    </>
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedWsId: number | null;
  onSelectWs: (id: number) => void;
}

function AmplifiersBody({
  workspaces,
  selectedWsId,
  onSelectWs,
}: BodyProps) {
  const qc = useQueryClient();
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedWsId) ?? workspaces[0],
    [workspaces, selectedWsId]
  );
  const activeWsId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  useEnsureActiveWorkspace(activeWsId);

  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();

  const { data: pubsResp, isLoading: pubsLoading, error: pubsError } =
    useApiQuery<PublicationsResponse>(
      ["publications", activeWsId],
      `/publications?agencyclient_id=${activeWsId}`
    );
  const publications = pubsResp?.data ?? [];

  const [selectedPubId, setSelectedPubId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(AMP_PUB_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedPubId != null) {
      try {
        window.sessionStorage.setItem(AMP_PUB_KEY, String(selectedPubId));
      } catch {
        /* tolerate */
      }
    }
  }, [selectedPubId]);

  useEffect(() => {
    if (publications.length === 0) return;
    const stillValid = publications.some((p) => p.id === selectedPubId);
    if (!stillValid) setSelectedPubId(publications[0].id);
  }, [publications, selectedPubId]);

  // Amplifiers — list scoped to the active publication.
  const ampsQuery = useApiQuery<AmplifiersResponse>(
    ["amplifiers", selectedPubId ?? 0],
    `/amplifiers?publication_id=${selectedPubId ?? 0}`,
    { enabled: selectedPubId != null }
  );
  const amplifiers = ampsQuery.data?.data ?? [];

  // Amplification rules — list scoped to the active publication.
  const rulesQuery = useApiQuery<AmplificationRulesResponse>(
    ["amplification-rules", selectedPubId ?? 0],
    `/amplification-rules?publication_id=${selectedPubId ?? 0}`,
    { enabled: selectedPubId != null }
  );
  const rules = rulesQuery.data?.data ?? [];

  // Voice profiles — feed the voice override dropdown on the amplifier
  // create/edit form.
  const voicesQuery = useApiQuery<VoiceProfilesResponse>(
    ["voice-profiles", activeWsId],
    `/voice-profiles?agencyclient_id=${activeWsId}`
  );
  const voices = voicesQuery.data?.data ?? [];

  // S-401 (CCM-79) — accounts (owned + shared/granted) for the rule step
  // picker. Same endpoint SocialAccountsView.tsx uses; a granted account is
  // now usable in a step (findUnownedStepAccountIds, amplification-rules.ts).
  const ruleAccountsQuery = useApiQuery<{ data: RuleAccountOption[] }>(
    ["social-accounts", activeWsId],
    `/agencyclients/${activeWsId}/social-accounts`
  );
  const ruleAccounts = ruleAccountsQuery.data?.data ?? [];

  const [showCreateAmp, setShowCreateAmp] = useState(false);
  const [editingAmp, setEditingAmp] = useState<Amplifier | null>(null);
  const [confirmDeleteAmp, setConfirmDeleteAmp] = useState<Amplifier | null>(
    null
  );

  const [showCreateRule, setShowCreateRule] = useState(false);
  const [editingRule, setEditingRule] = useState<AmplificationRule | null>(
    null
  );
  const [confirmDeleteRule, setConfirmDeleteRule] =
    useState<AmplificationRule | null>(null);

  const [pendingWsId, setPendingWsId] = useState<number | null>(null);

  function invalidateAmps() {
    void qc.invalidateQueries({
      queryKey: ["amplifiers", selectedPubId ?? 0],
    });
    // Kept after S-401 even though rule steps no longer reference this row:
    // both lists are publication-scoped and rendered together, so a stale
    // rules card next to a freshly-edited override reads as a bug.
    void qc.invalidateQueries({
      queryKey: ["amplification-rules", selectedPubId ?? 0],
    });
  }

  function invalidateRules() {
    void qc.invalidateQueries({
      queryKey: ["amplification-rules", selectedPubId ?? 0],
    });
  }

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeWsId) return;
    clearError();
    setPendingWsId(nextId);
    try {
      await activate(nextId);
      onSelectWs(nextId);
      setSelectedPubId(null);
    } catch {
      /* useActiveWorkspace surfaces error via switchError */
    } finally {
      setPendingWsId(null);
    }
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <Header />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingWsId ?? activeWsId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && (
            <span className="team-toolbar-hint">Switching workspace…</span>
          )}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
        </div>

        {!pubsLoading && !pubsError && publications.length > 0 && (
          <div className="team-toolbar">
            <span className="team-toolbar-label">Publication</span>
            <select
              className="settings-select"
              value={selectedPubId ?? publications[0].id}
              onChange={(e) => setSelectedPubId(Number(e.target.value))}
            >
              {publications.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
          </div>
        )}

        {pubsLoading && (
          <div className="state-loading">Loading publications…</div>
        )}

        {pubsError && !pubsLoading && (
          <div className="state-error">
            {pubsError.message ?? "Could not load publications."}
          </div>
        )}

        {!pubsLoading && !pubsError && publications.length === 0 && (
          <div className="settings-empty">
            <h2>No publications in this workspace</h2>
            <p>
              Amplifiers attach to publications. Create a publication first.
            </p>
            <Link to="/settings/publications" className="btn-primary">
              Go to publications
            </Link>
          </div>
        )}

        {selectedPubId != null && (
          <>
            {/* ----- Voice Overrides section (FEAT-095 / S-401, ADR-019) ----- */}
            <section>
              <div className="amp-rules-header" style={{ marginTop: 24 }}>
                <div>
                  <h2>Voice Overrides</h2>
                  <span className="amp-rules-header-sub">
                    Named voice overrides for this publication — each can
                    replace the publication's default voice profile.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setShowCreateAmp(true)}
                  disabled={isActivating}
                >
                  + New voice override
                </button>
              </div>

              {ampsQuery.isLoading && (
                <div className="state-loading">Loading voice overrides…</div>
              )}

              {ampsQuery.error && !ampsQuery.isLoading && (
                <div className="state-error">
                  {ampsQuery.error.message ?? "Could not load voice overrides."}
                </div>
              )}

              {!ampsQuery.isLoading &&
                !ampsQuery.error &&
                amplifiers.length === 0 && (
                  <div className="settings-empty">
                    <h2>No voice overrides yet</h2>
                    <p>
                      Add one to give this publication an alternate voice
                      profile when its pieces are amplified.
                    </p>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setShowCreateAmp(true)}
                    >
                      + New voice override
                    </button>
                  </div>
                )}

              {!ampsQuery.isLoading &&
                !ampsQuery.error &&
                amplifiers.length > 0 && (
                  <div className="amp-card-grid">
                    {amplifiers.map((amp) => (
                      <AmplifierCard
                        key={amp.id}
                        amp={amp}
                        voices={voices}
                        onEdit={() => setEditingAmp(amp)}
                        onDelete={() => setConfirmDeleteAmp(amp)}
                      />
                    ))}
                  </div>
                )}
            </section>

            {/* ----- Amplification rules section ----- */}
            <section className="amp-rules-section">
              <div className="amp-rules-header">
                <div>
                  <h2>Amplification rules</h2>
                  <span className="amp-rules-header-sub">
                    Cascades that fire when a piece publishes. Each step
                    posts to one connected social account.
                  </span>
                </div>
                {/* FEAT-095 / S-401 — gated on connected ACCOUNTS, not on
                    amplifiers: a rule step references an account id now, so
                    the old amplifiers.length check blocked rule creation for
                    a workspace that had accounts but no voice override. */}
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setShowCreateRule(true)}
                  disabled={ruleAccounts.length === 0 || isActivating}
                  title={
                    ruleAccounts.length === 0
                      ? "Connect a social account first (Settings → Social Accounts)"
                      : undefined
                  }
                >
                  + New rule
                </button>
              </div>

              {rulesQuery.isLoading && (
                <div className="state-loading">Loading rules…</div>
              )}

              {rulesQuery.error && !rulesQuery.isLoading && (
                <div className="state-error">
                  {rulesQuery.error.message ?? "Could not load rules."}
                </div>
              )}

              {!rulesQuery.isLoading &&
                !rulesQuery.error &&
                rules.length === 0 && (
                  <div className="settings-empty">
                    <p>No amplification rules yet.</p>
                  </div>
                )}

              {!rulesQuery.isLoading &&
                !rulesQuery.error &&
                rules.length > 0 && (
                  <RulesTable
                    rows={rules}
                    accounts={ruleAccounts}
                    onEdit={setEditingRule}
                    onDelete={setConfirmDeleteRule}
                  />
                )}
            </section>
          </>
        )}
      </div>

      {showCreateAmp && selectedPubId != null && (
        <CreateAmplifierModal
          agencyclientId={activeWsId}
          publicationId={selectedPubId}
          voices={voices}
          onClose={() => setShowCreateAmp(false)}
          onCreated={(created) => {
            invalidateAmps();
            setShowCreateAmp(false);
            // 2026-08-30 — land straight in the edit drawer for the amplifier
            // just created, so connecting a dispatch account happens in the
            // same flow instead of a separate trip back through the card
            // grid (the "process it from within that window" the founder
            // asked for — connecting itself still needs the amplifier's own
            // id, which doesn't exist until this POST returns).
            setEditingAmp(created);
          }}
        />
      )}

      {editingAmp && (
        <EditAmplifierDrawer
          amplifier={editingAmp}
          voices={voices}
          onClose={() => setEditingAmp(null)}
          onSaved={() => {
            invalidateAmps();
            setEditingAmp(null);
          }}
        />
      )}

      {confirmDeleteAmp && (
        <ConfirmDeleteAmpModal
          amplifier={confirmDeleteAmp}
          onCancel={() => setConfirmDeleteAmp(null)}
          onDeleted={() => {
            invalidateAmps();
            setConfirmDeleteAmp(null);
          }}
        />
      )}

      {showCreateRule && selectedPubId != null && (
        <CreateRuleModal
          agencyclientId={activeWsId}
          publicationId={selectedPubId}
          accounts={ruleAccounts}
          onClose={() => setShowCreateRule(false)}
          onCreated={() => {
            invalidateRules();
            setShowCreateRule(false);
          }}
        />
      )}

      {editingRule && (
        <EditRuleDrawer
          rule={editingRule}
          accounts={ruleAccounts}
          onClose={() => setEditingRule(null)}
          onSaved={() => {
            invalidateRules();
            setEditingRule(null);
          }}
        />
      )}

      {confirmDeleteRule && (
        <ConfirmDeleteRuleModal
          rule={confirmDeleteRule}
          onCancel={() => setConfirmDeleteRule(null)}
          onDeleted={() => {
            invalidateRules();
            setConfirmDeleteRule(null);
          }}
        />
      )}
    </main>
  );
}

// ---------- Voice override card ----------

interface AmpCardProps {
  amp: Amplifier;
  voices: VoiceProfile[];
  onEdit: () => void;
  onDelete: () => void;
}

function AmplifierCard({ amp, voices, onEdit, onDelete }: AmpCardProps) {
  const voiceId = ampVoiceOverrideId(amp);
  const voiceName = voiceId
    ? (voices.find((v) => v.id === voiceId)?.name ?? `Voice #${voiceId}`)
    : null;
  return (
    <div className="amp-card" onClick={onEdit}>
      <div className="amp-card-header">
        <div>
          <h3 className="amp-card-title">{ampDisplayName(amp)}</h3>
          <div className="amp-card-slug">
            <code className="mono">{amp.slug}</code>
          </div>
        </div>
      </div>
      <div className="amp-card-slug">
        {voiceName ? (
          <>
            Voice override: <strong>{voiceName}</strong>
          </>
        ) : (
          <span className="pill pill-muted">Uses publication default voice</span>
        )}
      </div>
      <div className="amp-card-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn-ghost" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn-ghost btn-danger"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ---------- Rules table ----------

interface RulesTableProps {
  rows: AmplificationRule[];
  accounts: RuleAccountOption[];
  onEdit: (r: AmplificationRule) => void;
  onDelete: (r: AmplificationRule) => void;
}

function RulesTable({ rows, accounts, onEdit, onDelete }: RulesTableProps) {
  const accountsById = new Map(accounts.map((a) => [a.id, a]));
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Slug</th>
          <th>Steps</th>
          <th className="ws-actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.id}
            className="workspaces-row"
            onClick={() => onEdit(r)}
          >
            <td className="ws-name-cell">
              <span className="ws-name">{r.name}</span>
            </td>
            <td>
              <code className="mono ws-slug">{r.slug}</code>
            </td>
            <td>
              {(r.steps?.length ?? 0) === 0 ? (
                <span className="pill pill-muted">No steps</span>
              ) : (
                <span>
                  {r.steps!.length} step{r.steps!.length === 1 ? "" : "s"}:
                  &nbsp;
                  <code className="mono ws-slug">
                    {r.steps!
                      .map((s) => {
                        const account = accountsById.get(s.account_id);
                        const label = account
                          ? `${PLATFORM_LABEL[account.platform as Platform] ?? account.platform} — ${account.display_name}`
                          : `account #${s.account_id}`;
                        return `${label}${s.offset_days ? `(+${s.offset_days}d)` : ""}`;
                      })
                      .join(", ")}
                  </code>
                </span>
              )}
            </td>
            <td className="ws-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => onEdit(r)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-danger"
                onClick={() => onDelete(r)}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Amplifier create modal ----------

interface CreateAmpProps {
  agencyclientId: number;
  publicationId: number;
  voices: VoiceProfile[];
  onClose: () => void;
  onCreated: (created: Amplifier) => void;
}

interface AmpFormState {
  displayName: string;
  slug: string;
  voiceOverrideId: string; // "" or "<id>"
  linkedinPreferredTime: string;
  linkedinTimeZone: string;
  linkedinAmpOffsetDays: string;
}

const EMPTY_AMP_FORM: AmpFormState = {
  displayName: "",
  slug: "",
  voiceOverrideId: "",
  linkedinPreferredTime: "",
  linkedinTimeZone: "",
  linkedinAmpOffsetDays: "0",
};

interface CreateAmpBody {
  agencyclient_id: number;
  publication_id: number;
  slug: string;
  display_name: string;
  voice_profile_override_id?: number;
  linkedin_preferred_time?: string;
  linkedin_time_zone?: string;
  linkedin_amp_offset_days?: number;
}

function CreateAmplifierModal({
  agencyclientId,
  publicationId,
  voices,
  onClose,
  onCreated,
}: CreateAmpProps) {
  const api = useApi();
  const [form, setForm] = useState<AmpFormState>(EMPTY_AMP_FORM);
  const [slugDirty, setSlugDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!slugDirty) {
      setForm((f) => ({ ...f, slug: slugify(f.displayName) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.displayName, slugDirty]);

  const nameError =
    form.displayName.trim().length === 0 ||
    form.displayName.trim().length > 120;
  const slugError = form.slug.length > 0 && !isValidSlug(form.slug);
  const offsetError =
    form.linkedinAmpOffsetDays.length > 0 &&
    !Number.isFinite(Number(form.linkedinAmpOffsetDays));
  const collisionError =
    serverError instanceof ApiError && serverError.status === 409;
  const validationError =
    serverError instanceof ApiError && serverError.status === 422;
  const canSubmit =
    !submitting &&
    !nameError &&
    !slugError &&
    !offsetError &&
    form.slug.length > 0;

  function trimToOptional(value: string): string | undefined {
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const body: CreateAmpBody = {
        agencyclient_id: agencyclientId,
        publication_id: publicationId,
        slug: form.slug,
        display_name: form.displayName.trim(),
      };
      const v = form.voiceOverrideId.trim();
      if (v.length > 0) body.voice_profile_override_id = Number(v);
      const lpt = trimToOptional(form.linkedinPreferredTime);
      if (lpt) body.linkedin_preferred_time = lpt;
      const ltz = trimToOptional(form.linkedinTimeZone);
      if (ltz) body.linkedin_time_zone = ltz;
      if (form.linkedinAmpOffsetDays.trim().length > 0) {
        body.linkedin_amp_offset_days = Number(form.linkedinAmpOffsetDays);
      }

      const created = await api<Amplifier>("/amplifiers", {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err);
      else
        setServerError(
          new ApiError(0, String(err), "Could not create voice override")
        );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Voice Overrides" title="New voice override">
      <form className="settings-form" onSubmit={submit}>
        <AmplifierFormFields
          form={form}
          setForm={setForm}
          setSlugDirty={setSlugDirty}
          voices={voices}
          nameError={nameError}
          slugError={slugError}
          offsetError={offsetError}
          collisionError={collisionError}
        />
        {validationError && !collisionError && (
          <div className="settings-form-error">
            {serverError?.body || "The server rejected the request."}
          </div>
        )}
        {serverError &&
          !collisionError &&
          !validationError &&
          serverError.status !== 409 && (
            <div className="settings-form-error">{serverError.message}</div>
          )}
        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create voice override"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Amplifier edit drawer ----------

interface EditAmpProps {
  amplifier: Amplifier;
  voices: VoiceProfile[];
  onClose: () => void;
  onSaved: () => void;
}

interface UpdateAmpBody {
  slug?: string;
  display_name?: string;
  voice_profile_override_id?: number;
  linkedin_preferred_time?: string;
  linkedin_time_zone?: string;
  linkedin_amp_offset_days?: number;
}

function ampToForm(amp: Amplifier): AmpFormState {
  return {
    displayName: ampDisplayName(amp),
    slug: amp.slug,
    voiceOverrideId:
      ampVoiceOverrideId(amp) != null ? String(ampVoiceOverrideId(amp)) : "",
    linkedinPreferredTime: readAmplifierField<string>(
      amp,
      "linkedinPreferredTime",
      "linkedin_preferred_time",
      ""
    ),
    linkedinTimeZone: readAmplifierField<string>(
      amp,
      "linkedinTimeZone",
      "linkedin_time_zone",
      ""
    ),
    linkedinAmpOffsetDays: String(
      readAmplifierField<number>(
        amp,
        "linkedinAmpOffsetDays",
        "linkedin_amp_offset_days",
        0
      ) ?? 0
    ),
  };
}

function EditAmplifierDrawer({
  amplifier,
  voices,
  onClose,
  onSaved,
}: EditAmpProps) {
  const initial = useMemo(() => ampToForm(amplifier), [amplifier]);
  const [form, setForm] = useState<AmpFormState>(initial);
  const [recentlySaved, setRecentlySaved] = useState(false);

  // No slug auto-derive on edit — preserve the existing slug unless the
  // user explicitly edits it.

  const mutation = useApiMutation<Amplifier, UpdateAmpBody>(
    `/amplifiers/${amplifier.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        window.setTimeout(() => onSaved(), 600);
      },
    }
  );

  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  const dirty = useMemo(() => {
    return (
      form.displayName.trim() !== initial.displayName.trim() ||
      form.slug !== initial.slug ||
      form.voiceOverrideId !== initial.voiceOverrideId ||
      form.linkedinPreferredTime !== initial.linkedinPreferredTime ||
      form.linkedinTimeZone !== initial.linkedinTimeZone ||
      form.linkedinAmpOffsetDays !== initial.linkedinAmpOffsetDays
    );
  }, [form, initial]);

  const nameError =
    form.displayName.trim().length === 0 ||
    form.displayName.trim().length > 120;
  const slugError = form.slug.length > 0 && !isValidSlug(form.slug);
  const offsetError =
    form.linkedinAmpOffsetDays.length > 0 &&
    !Number.isFinite(Number(form.linkedinAmpOffsetDays));
  const collisionError =
    mutation.error instanceof ApiError && mutation.error.status === 409;
  const canSubmit =
    !mutation.isPending && dirty && !nameError && !slugError && !offsetError;

  function buildPatch(): UpdateAmpBody {
    const body: UpdateAmpBody = {};
    if (form.displayName.trim() !== initial.displayName.trim()) {
      body.display_name = form.displayName.trim();
    }
    if (form.slug !== initial.slug) body.slug = form.slug;
    if (form.voiceOverrideId !== initial.voiceOverrideId) {
      if (form.voiceOverrideId.trim().length > 0) {
        body.voice_profile_override_id = Number(form.voiceOverrideId);
      }
      // Note: API has no clear-FK affordance; setting back to null isn't
      // possible via PATCH today. Leaving the override as-is when the
      // dropdown is cleared keeps current behavior.
    }
    if (form.linkedinPreferredTime !== initial.linkedinPreferredTime) {
      body.linkedin_preferred_time = form.linkedinPreferredTime.trim();
    }
    if (form.linkedinTimeZone !== initial.linkedinTimeZone) {
      body.linkedin_time_zone = form.linkedinTimeZone.trim();
    }
    if (form.linkedinAmpOffsetDays !== initial.linkedinAmpOffsetDays) {
      body.linkedin_amp_offset_days = Number(form.linkedinAmpOffsetDays);
    }
    return body;
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate(buildPatch());
  }

  return (
    <DrawerShell onClose={onClose} eyebrow="Voice Overrides" title="Edit voice override">
      <form className="settings-form" onSubmit={submit}>
        <AmplifierFormFields
          form={form}
          setForm={setForm}
          setSlugDirty={() => {
            /* slug never auto-derives on edit */
          }}
          voices={voices}
          nameError={nameError}
          slugError={slugError}
          offsetError={offsetError}
          collisionError={collisionError}
        />

        {mutation.error && !collisionError && (
          <div className="settings-form-error">
            {mutation.error.body || mutation.error.message}
          </div>
        )}

        <div className="settings-form-actions">
          {recentlySaved && (
            <span className="editor-saved-badge" role="status">
              Saved
            </span>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ---------- Shared voice-override form fields ----------

interface AmpFormFieldsProps {
  form: AmpFormState;
  setForm: React.Dispatch<React.SetStateAction<AmpFormState>>;
  setSlugDirty: (b: boolean) => void;
  voices: VoiceProfile[];
  nameError: boolean;
  slugError: boolean;
  offsetError: boolean;
  collisionError: boolean;
}

function AmplifierFormFields({
  form,
  setForm,
  setSlugDirty,
  voices,
  nameError,
  slugError,
  offsetError,
  collisionError,
}: AmpFormFieldsProps) {
  return (
    <>
      <label className="settings-field">
        <span className="settings-label">Display name</span>
        <input
          className="settings-input"
          type="text"
          value={form.displayName}
          maxLength={120}
          onChange={(e) =>
            setForm((f) => ({ ...f, displayName: e.target.value }))
          }
          autoFocus
          placeholder="e.g. Luis Capobianco (LinkedIn)"
        />
        {nameError && form.displayName.length > 0 && (
          <span className="settings-field-error">
            Display name must be 1–120 characters.
          </span>
        )}
      </label>

      <label className="settings-field">
        <span className="settings-label">
          Slug{" "}
          <span className="settings-label-hint">
            Stable identifier for this override
          </span>
        </span>
        <input
          className="settings-input mono"
          type="text"
          value={form.slug}
          maxLength={80}
          onChange={(e) => {
            setForm((f) => ({ ...f, slug: e.target.value }));
            setSlugDirty(true);
          }}
          placeholder="luis-linkedin"
        />
        {slugError && (
          <span className="settings-field-error">
            Kebab-case only: lowercase letters, digits, and hyphens.
          </span>
        )}
        {collisionError && (
          <span className="settings-field-error">
            That slug is already taken for this publication.
          </span>
        )}
      </label>

      <label className="settings-field">
        <span className="settings-label">
          Voice profile override{" "}
          <span className="settings-label-hint">
            Optional — replaces the publication's default voice when this
            override is in play
          </span>
        </span>
        <select
          className="settings-select"
          value={form.voiceOverrideId}
          onChange={(e) =>
            setForm((f) => ({ ...f, voiceOverrideId: e.target.value }))
          }
        >
          <option value="">(use publication default)</option>
          {voices.map((v) => (
            <option key={v.id} value={String(v.id)}>
              {v.name}
            </option>
          ))}
        </select>
        {voices.length === 0 && (
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            No voice profiles in this workspace yet. Create one under
            Settings → Publications.
          </span>
        )}
      </label>

      <details className="editor-disclosure" open>
        <summary className="editor-disclosure-summary">LinkedIn timing</summary>
        <div className="editor-disclosure-body">
          <label className="settings-field">
            <span className="settings-label">
              Preferred time of day{" "}
              <span className="settings-label-hint">HH:MM (24h)</span>
            </span>
            <input
              className="settings-input mono"
              type="text"
              value={form.linkedinPreferredTime}
              maxLength={64}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  linkedinPreferredTime: e.target.value,
                }))
              }
              placeholder="12:45"
            />
          </label>
          <label className="settings-field">
            <span className="settings-label">
              Time zone{" "}
              <span className="settings-label-hint">IANA, e.g. America/New_York</span>
            </span>
            <input
              className="settings-input mono"
              type="text"
              value={form.linkedinTimeZone}
              maxLength={64}
              onChange={(e) =>
                setForm((f) => ({ ...f, linkedinTimeZone: e.target.value }))
              }
              placeholder="America/New_York"
            />
          </label>
          <label className="settings-field">
            <span className="settings-label">
              Amplification offset (days){" "}
              <span className="settings-label-hint">
                Days after piece publish (–30 to 30)
              </span>
            </span>
            <input
              className="settings-input mono"
              type="number"
              min={-30}
              max={30}
              value={form.linkedinAmpOffsetDays}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  linkedinAmpOffsetDays: e.target.value,
                }))
              }
            />
            {offsetError && (
              <span className="settings-field-error">
                Offset must be a number between –30 and 30.
              </span>
            )}
          </label>
        </div>
      </details>

    </>
  );
}

// ---------- Amplifier delete confirm ----------

interface ConfirmDeleteAmpProps {
  amplifier: Amplifier;
  onCancel: () => void;
  onDeleted: () => void;
}

function ConfirmDeleteAmpModal({
  amplifier,
  onCancel,
  onDeleted,
}: ConfirmDeleteAmpProps) {
  const mutation = useApiMutation<void, void>(
    `/amplifiers/${amplifier.id}`,
    "DELETE",
    {
      onSuccess: () => onDeleted(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="Voice Overrides · danger" title="Delete voice override?">
      <p className="settings-confirm-copy">
        Remove <strong>{ampDisplayName(amplifier)}</strong>? Publications
        using it fall back to their default voice profile. Amplification
        rules are unaffected — they reference connected accounts, not this.
        Warning: any social post created before the account migration that
        still points at this row is hard-deleted with it (ON DELETE CASCADE
        — the same cascade behind BUG-088).
      </p>
      {mutation.error && (
        <div className="settings-form-error">{mutation.error.message}</div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Deleting…" : "Delete voice override"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- Rule create modal ----------

interface CreateRuleProps {
  agencyclientId: number;
  publicationId: number;
  accounts: RuleAccountOption[];
  onClose: () => void;
  onCreated: () => void;
}

interface RuleFormState {
  name: string;
  slug: string;
  steps: AmplificationStep[];
}

const EMPTY_RULE_FORM: RuleFormState = {
  name: "",
  slug: "",
  steps: [],
};

interface CreateRuleBody {
  agencyclient_id: number;
  publication_id: number;
  slug: string;
  name: string;
  steps?: AmplificationStep[];
}

function CreateRuleModal({
  agencyclientId,
  publicationId,
  accounts,
  onClose,
  onCreated,
}: CreateRuleProps) {
  const api = useApi();
  const [form, setForm] = useState<RuleFormState>(EMPTY_RULE_FORM);
  const [slugDirty, setSlugDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!slugDirty) {
      setForm((f) => ({ ...f, slug: slugify(f.name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, slugDirty]);

  const nameError =
    form.name.trim().length === 0 || form.name.trim().length > 120;
  const slugError = form.slug.length > 0 && !isValidSlug(form.slug);
  const collisionError =
    serverError instanceof ApiError && serverError.status === 409;
  const validationError =
    serverError instanceof ApiError && serverError.status === 422;
  const canSubmit =
    !submitting && !nameError && !slugError && form.slug.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const body: CreateRuleBody = {
        agencyclient_id: agencyclientId,
        publication_id: publicationId,
        slug: form.slug,
        name: form.name.trim(),
        steps: cleanSteps(form.steps),
      };
      await api<AmplificationRule>("/amplification-rules", {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) setServerError(err);
      else
        setServerError(
          new ApiError(0, String(err), "Could not create rule")
        );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Amplifier rule" title="New amplification rule">
      <form className="settings-form" onSubmit={submit}>
        <RuleFormFields
          form={form}
          setForm={setForm}
          setSlugDirty={setSlugDirty}
          accounts={accounts}
          nameError={nameError}
          slugError={slugError}
          collisionError={collisionError}
        />
        {validationError && !collisionError && (
          <div className="settings-form-error">
            {serverError?.body || "The server rejected the request."}
          </div>
        )}
        {serverError &&
          !collisionError &&
          !validationError &&
          serverError.status !== 409 && (
            <div className="settings-form-error">{serverError.message}</div>
          )}
        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create rule"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Rule edit drawer ----------

interface EditRuleProps {
  rule: AmplificationRule;
  accounts: RuleAccountOption[];
  onClose: () => void;
  onSaved: () => void;
}

interface UpdateRuleBody {
  slug?: string;
  name?: string;
  steps?: AmplificationStep[];
}

// BUG-112 — every edge of the form (load, create, update) goes through
// toEditableStep, so a hidden field on a stored step can never ride along.
// A legacy step with no account gets 0, as addStep does with no accounts;
// the API refuses it, so the person has to pick one.
function cleanSteps(steps: readonly object[]): AmplificationStep[] {
  return steps.map((s) => {
    const step = toEditableStep(s);
    return { ...step, account_id: step.account_id ?? 0 };
  });
}

function ruleToForm(rule: AmplificationRule): RuleFormState {
  return {
    name: rule.name,
    slug: rule.slug,
    steps: rule.steps ? cleanSteps(rule.steps) : [],
  };
}

function stepsEqual(a: AmplificationStep[], b: AmplificationStep[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].account_id !== b[i].account_id ||
      (a[i].platform ?? null) !== (b[i].platform ?? null) ||
      (a[i].offset_days ?? null) !== (b[i].offset_days ?? null) ||
      (a[i].time_of_day ?? "") !== (b[i].time_of_day ?? "")
    ) {
      return false;
    }
  }
  return true;
}

function EditRuleDrawer({
  rule,
  accounts,
  onClose,
  onSaved,
}: EditRuleProps) {
  const initial = useMemo(() => ruleToForm(rule), [rule]);
  const [form, setForm] = useState<RuleFormState>(initial);
  const [recentlySaved, setRecentlySaved] = useState(false);

  const mutation = useApiMutation<AmplificationRule, UpdateRuleBody>(
    `/amplification-rules/${rule.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setRecentlySaved(true);
        window.setTimeout(() => onSaved(), 600);
      },
    }
  );

  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  const dirty =
    form.name.trim() !== initial.name ||
    form.slug !== initial.slug ||
    !stepsEqual(form.steps, initial.steps);

  const nameError =
    form.name.trim().length === 0 || form.name.trim().length > 120;
  const slugError = form.slug.length > 0 && !isValidSlug(form.slug);
  const collisionError =
    mutation.error instanceof ApiError && mutation.error.status === 409;
  const canSubmit =
    !mutation.isPending && dirty && !nameError && !slugError;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const body: UpdateRuleBody = {};
    if (form.name.trim() !== initial.name) body.name = form.name.trim();
    if (form.slug !== initial.slug) body.slug = form.slug;
    if (!stepsEqual(form.steps, initial.steps)) body.steps = cleanSteps(form.steps);
    mutation.mutate(body);
  }

  return (
    <DrawerShell onClose={onClose} eyebrow="Amplifier rule" title="Edit amplification rule">
      <form className="settings-form" onSubmit={submit}>
        <RuleFormFields
          form={form}
          setForm={setForm}
          setSlugDirty={() => {
            /* no-op on edit */
          }}
          accounts={accounts}
          nameError={nameError}
          slugError={slugError}
          collisionError={collisionError}
        />

        {mutation.error && !collisionError && (
          <div className="settings-form-error">
            {mutation.error.body || mutation.error.message}
          </div>
        )}

        <div className="settings-form-actions">
          {recentlySaved && (
            <span className="editor-saved-badge" role="status">
              Saved
            </span>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ---------- Shared rule form fields ----------

interface RuleFormFieldsProps {
  form: RuleFormState;
  setForm: React.Dispatch<React.SetStateAction<RuleFormState>>;
  setSlugDirty: (b: boolean) => void;
  accounts: RuleAccountOption[];
  nameError: boolean;
  slugError: boolean;
  collisionError: boolean;
}

function RuleFormFields({
  form,
  setForm,
  setSlugDirty,
  accounts,
  nameError,
  slugError,
  collisionError,
}: RuleFormFieldsProps) {
  function addStep() {
    setForm((f) => ({
      ...f,
      steps: [
        ...f.steps,
        {
          account_id: accounts[0]?.id ?? 0,
          offset_days: 0,
          time_of_day: "",
        },
      ],
    }));
  }

  function updateStep(i: number, patch: Partial<AmplificationStep>) {
    setForm((f) => {
      const next = f.steps.slice();
      next[i] = { ...next[i], ...patch };
      return { ...f, steps: next };
    });
  }

  function removeStep(i: number) {
    setForm((f) => ({
      ...f,
      steps: f.steps.filter((_, j) => j !== i),
    }));
  }

  return (
    <>
      <label className="settings-field">
        <span className="settings-label">Name</span>
        <input
          className="settings-input"
          type="text"
          value={form.name}
          maxLength={120}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          autoFocus
          placeholder="e.g. Default fan-out"
        />
        {nameError && form.name.length > 0 && (
          <span className="settings-field-error">
            Name must be 1–120 characters.
          </span>
        )}
      </label>

      <label className="settings-field">
        <span className="settings-label">Slug</span>
        <input
          className="settings-input mono"
          type="text"
          value={form.slug}
          maxLength={80}
          onChange={(e) => {
            setForm((f) => ({ ...f, slug: e.target.value }));
            setSlugDirty(true);
          }}
          placeholder="default-fan-out"
        />
        {slugError && (
          <span className="settings-field-error">
            Kebab-case only: lowercase letters, digits, and hyphens.
          </span>
        )}
        {collisionError && (
          <span className="settings-field-error">
            That slug is already taken for this publication.
          </span>
        )}
      </label>

      <div className="settings-field">
        <span className="settings-label">
          Steps{" "}
          <span className="settings-label-hint">
            Each step fires an amplifier on a platform when the piece
            publishes. Steps run in order.
          </span>
        </span>
        {form.steps.length === 0 && (
          <div className="voice-rules-empty" style={{ marginBottom: 8 }}>
            No steps yet. Add one to fan content out.
          </div>
        )}
        {form.steps.map((step, i) => (
          <div className="amp-step-row" key={i}>
            <label className="settings-field">
              <span className="settings-label">Account</span>
              <select
                className="settings-select"
                value={step.account_id}
                onChange={(e) =>
                  updateStep(i, { account_id: Number(e.target.value) })
                }
              >
                {accounts.length === 0 ? (
                  <option value="">(no connected accounts)</option>
                ) : (
                  accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {PLATFORM_LABEL[a.platform as Platform] ?? a.platform} — {a.display_name}
                      {a.is_shared_to_me ? " (shared)" : ""}
                    </option>
                  ))
                )}
              </select>
              {step.platform === LINKEDIN_ARTICLE_PLATFORM && (
                <span className="settings-label-hint">LinkedIn article companion — posts with the article</span>
              )}
            </label>
            <label className="settings-field">
              <span className="settings-label">Offset (days)</span>
              <input
                className="settings-input mono"
                type="number"
                min={-30}
                max={30}
                value={step.offset_days ?? 0}
                onChange={(e) =>
                  updateStep(i, {
                    offset_days: Number(e.target.value),
                  })
                }
              />
            </label>
            <label className="settings-field">
              <span className="settings-label">Time (HH:MM)</span>
              <input
                className="settings-input mono"
                type="text"
                value={step.time_of_day ?? ""}
                maxLength={64}
                onChange={(e) =>
                  updateStep(i, { time_of_day: e.target.value })
                }
                placeholder="12:45"
              />
            </label>
            <button
              type="button"
              className="amp-step-remove"
              aria-label={`Remove step ${i + 1}`}
              title="Remove step"
              onClick={() => removeStep(i)}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost amp-step-add"
          onClick={addStep}
          disabled={accounts.length === 0}
          title={
            accounts.length === 0
              ? "Connect a social account first (Settings → Social Accounts)"
              : undefined
          }
        >
          + Add step
        </button>
      </div>
    </>
  );
}

// ---------- Rule delete confirm ----------

interface ConfirmDeleteRuleProps {
  rule: AmplificationRule;
  onCancel: () => void;
  onDeleted: () => void;
}

function ConfirmDeleteRuleModal({
  rule,
  onCancel,
  onDeleted,
}: ConfirmDeleteRuleProps) {
  const mutation = useApiMutation<void, void>(
    `/amplification-rules/${rule.id}`,
    "DELETE",
    {
      onSuccess: () => onDeleted(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="Amplifier rule · danger" title="Delete amplification rule?">
      <p className="settings-confirm-copy">
        Remove the <strong>{rule.name}</strong> rule? Future piece
        publishes will no longer fire its steps. Existing social_calendar
        rows scheduled from past runs are not affected.
      </p>
      {mutation.error && (
        <div className="settings-form-error">{mutation.error.message}</div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Deleting…" : "Delete rule"}
        </button>
      </div>
    </ModalShell>
  );
}

// NOTE: the local ModalShell / DrawerShell that used to live here were removed
// in the option-3 consolidation — both now come from
// @/components/SettingsModalShell (imported at the top of this file). The shared
// shell renders the canonical anatomy (eyebrow + 24px serif title, bordered
// close, blurred overlay) and handles Escape/backdrop close.
